import { TKV2_ORDER_ATTACHMENT_API, Tkv2Property, expected } from '@aloft/toolkit-shared';
import {
  Updater,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import {
  fetchGetTkv2OrderSubjectAPI,
  useUpdateTkv2OrderSubjectAPI,
} from '../../hooks/api/toolkitComponents.js';
import { useToolkitContext } from '../../hooks/api/toolkitContext.js';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.js';
import { getTkv2SubjectQueryKey } from '../../utils/getTkv2SubjectQueryKey.js';

const schema = TKV2_ORDER_ATTACHMENT_API.SUBJECT.GET.RESPONSE;
type QueryData = z.infer<typeof schema>;

export function useTkv2Subject(
  params: { pathParams: { orderID: string } },
  opts?: UseQueryOptions<QueryData>,
): [
  UseQueryResult<QueryData>,
  (updater: Updater<QueryData, QueryData>) => void,
  (updater: Tkv2Property) => void,
  boolean,
] {
  const { fetcherOptions } = useToolkitContext();

  const [dirty, setDirty] = useState<QueryData | undefined>(undefined);
  const isDirty = dirty !== undefined;

  const queryKey = useMemo(
    () => getTkv2SubjectQueryKey(params.pathParams.orderID),
    [params.pathParams.orderID],
  );
  const queryClient = useQueryClient();

  const getQueryData = useCallback(
    () => expected(queryClient.getQueryData(queryKey) as z.infer<typeof schema>),
    [queryKey, queryClient],
  );
  const setQueryData = useCallback(
    (attach: QueryData) => queryClient.setQueryData(queryKey, attach),
    [queryKey, queryClient],
  );

  const query = useQuery<QueryData>(
    queryKey,
    async () => {
      const res = await fetchGetTkv2OrderSubjectAPI({ ...fetcherOptions, ...params });
      return schema.parse(res);
    },
    {
      ...opts,
      refetchInterval: isDirty ? false : opts?.refetchInterval,
    },
  );

  const { mutate: mutateSubject, isLoading: isLoadingSubject } = useUpdateTkv2OrderSubjectAPI({
    onSuccess(data) {
      setQueryData(TKV2_ORDER_ATTACHMENT_API.SUBJECT.PUT.RESPONSE.parse(data));
    },
  });

  const mutationTrigger = useEdgeTrigger({
    condition: !isLoadingSubject && !query.isInitialLoading && isDirty,
    delayMs: 1000,
    action: () => {
      const optimistic = expected(dirty, 'isDirty true when dirty undefined');
      setQueryData(optimistic);
      setDirty(undefined);
      mutateSubject({ ...params, body: optimistic.body });
    },
  });

  const setter = useCallback(
    (updater: Updater<QueryData, QueryData>) => {
      mutationTrigger.restartTimer();
      setDirty(
        typeof updater === 'function'
          ? (dirty: QueryData | undefined) => updater(dirty ?? getQueryData())
          : updater,
      );
    },
    [mutationTrigger, setDirty, getQueryData],
  );

  const updater = useCallback(
    (subj: Tkv2Property): void => {
      queryClient.cancelQueries({ queryKey });
      mutateSubject({ ...params, body: subj });
    },
    [queryClient, queryKey, mutateSubject, params],
  );

  const queryData = query.data;
  const shadowedQueryData = queryData !== undefined ? (dirty ?? queryData) : undefined;

  // @ts-expect-error UseQueryResult has complicated conditional types around
  // whether 'data' is defined or not, and it would be a mess to try to do it
  // in a way that typescript can verify automatically. because
  // shadowQueryData is always defined when data is defined we have nothing to
  // worry about.
  const shadowedQuery: UseQueryResult<QueryData> = {
    ...query,
    data: shadowedQueryData,
  };

  return [shadowedQuery, setter, updater, isDirty || isLoadingSubject];
}
